

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Typography, Button, TextField, Box, Paper, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Checkbox, FormControlLabel, Alert, CircularProgress 
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../auth/Auth';

const PhraseReplacementPage = () => {
  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({
    name: '',
    phrase: ''
  });
  const [generalError, setGeneralError] = useState('');
  const [replacements, setReplacements] = useState({});
  const [newReplacement, setNewReplacement] = useState({
    name: '',
    phrase: '',
    contractorId: '',
    status: 1
  });
  const [multiplePhrases, setMultiplePhrases] = useState([{ phrase: '' }]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReplacement, setEditingReplacement] = useState(null);
  const [existingPhraseMode, setExistingPhraseMode] = useState(false);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [selectedGroupStatus, setSelectedGroupStatus] = useState(1);
  const [translations, setTranslations] = useState(null);
  const [language, setLanguage] = useState('uk-UA');
  const { user } = useAuth();

  useEffect(() => {
    if (user?.user_id) {
      fetchContractorData();
    }
  }, [user]);

  const fetchContractorData = async () => {
    try {
      const userId = user?.user_id;
      if (!userId) {
        setGeneralError('Користувач не авторизований');
        setLoading(false);
        return;
      }

      const regionResponse = await axios.get(`https://bank2.daolog.net/user-region/${userId}`);
      const userRegion = regionResponse.data.region;
      setLanguage(userRegion);

      const translationsResponse = await axios.get(`https://bank2.daolog.net/translations/phrases/${userRegion}`);
      setTranslations(translationsResponse.data);

      const contractorResponse = await axios.get(`https://bank2.daolog.net/user-contractor-mmm/${userId}`);
      const contractorData = contractorResponse.data;
      setContractor(contractorData);
      setNewReplacement(prev => ({ ...prev, contractorId: contractorData.id }));

      if (contractorData.id) {
        await fetchReplacements(contractorData.id);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching contractor data:', error);
      setGeneralError('Помилка при отриманні даних');
      setLoading(false);
    }
  };

  const fetchReplacements = async (contractorId) => {
    try {
      const response = await axios.get(`https://bank2.daolog.net/phrases-mmm/${contractorId}`);
      const data = response.data;
      
      const grouped = data.reduce((acc, replacement) => {
        if (!acc[replacement.name]) {
          acc[replacement.name] = [];
        }
        acc[replacement.name].push(replacement);
        return acc;
      }, {});
      
      setReplacements(grouped);
    } catch (error) {
      console.error('Error fetching replacements:', error);
      setGeneralError(translations?.errorFetchingReplacements || 'Помилка при отриманні фраз');
    }
  };

  const generateConfig = async (contractorId) => {
    try {
      await axios.post(`https://bank2.daolog.net/generate-phrases-config-mmm`, {
        contractorId: contractorId
      });
    } catch (error) {
      console.error('Error generating config:', error);
    }
  };

  const handlePhraseChange = (index, value) => {
    const newPhrases = [...multiplePhrases];
    newPhrases[index].phrase = value;
    setMultiplePhrases(newPhrases);
  };

  const handleAddPhrase = () => {
    setMultiplePhrases([...multiplePhrases, { phrase: '' }]);
  };

  const handleRemovePhrase = (index) => {
    const newPhrases = multiplePhrases.filter((_, i) => i !== index);
    setMultiplePhrases(newPhrases);
  };

  const handleOpenEditDialog = (replacement) => {
    setEditingReplacement({ ...replacement });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditingReplacement(null);
    setEditDialogOpen(false);
  };

  const handleExistingPhraseClick = (groupName, groupStatus) => {
    setExistingPhraseMode(true);
    setSelectedGroupName(groupName);
    setSelectedGroupStatus(groupStatus);
    setNewReplacement(prev => ({
      ...prev,
      name: groupName,
      status: groupStatus,
      phrase: ''
    }));
    setMultiplePhrases([{ phrase: '' }]);
  };

  const handleCancelAddPhrase = () => {
    setExistingPhraseMode(false);
    setSelectedGroupName('');
    setSelectedGroupStatus(1);
    setNewReplacement({
      name: '',
      phrase: '',
      contractorId: contractor?.id,
      status: 1
    });
    setMultiplePhrases([{ phrase: '' }]);
    setErrors({
      name: '',
      phrase: ''
    });
    setGeneralError('');
  };

  const handleStatusChange = async (name, newStatus) => {
    try {
      const response = await axios.put(`https://bank2.daolog.net/phrases/group-status-mmm/${encodeURIComponent(name)}`, {
        status: newStatus,
        userId: user?.user_id,
        contractorId: contractor?.id
      });

      if (response.data.success) {
        await fetchReplacements(contractor?.id);
        await generateConfig(contractor?.id);
      }
    } catch (error) {
      console.error('Error updating replacements status:', error);
      setGeneralError(translations?.errorUpdatingStatus || 'Помилка при оновленні статусу');
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: '',
      phrase: ''
    };

    if (!existingPhraseMode && !newReplacement.name.trim()) {
      newErrors.name = translations?.warning || 'Це поле обов\'язкове';
      isValid = false;
    }

    // Перевіряємо, чи всі фрази заповнені
    const emptyPhrases = multiplePhrases.some(p => !p.phrase.trim());
    if (emptyPhrases) {
      newErrors.phrase = translations?.warningPhrase || 'Ключова фраза обов\'язкова';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAddReplacement = async () => {
    if (!validateForm()) {
      return;
    }

    // Фільтруємо порожні фрази
    const validPhrases = multiplePhrases.filter(p => p.phrase.trim());
    
    if (validPhrases.length === 0) {
      setGeneralError(translations?.warningPhrase || 'Додайте хоча б одну ключову фразу');
      return;
    }

    try {
      // Створюємо масив промісів для всіх фраз
      const promises = validPhrases.map(phrase =>
        axios.post(`https://bank2.daolog.net/phrases-mmm`, {
          ...newReplacement,
          phrase: phrase.phrase,
          userId: user?.user_id
        })
      );

      // Виконуємо всі запити паралельно
      const results = await Promise.all(promises);
      
      // Перевіряємо, чи всі запити успішні
      const allSuccess = results.every(result => result.data.success !== false);
      
      if (allSuccess) {
        // Очищаємо форму тільки після успішного додавання всіх фраз
        setNewReplacement({
          name: '',
          phrase: '',
          contractorId: contractor?.id,
          status: 1
        });
        setMultiplePhrases([{ phrase: '' }]);
        setErrors({
          name: '',
          phrase: ''
        });
        setGeneralError('');
        setExistingPhraseMode(false);
        setSelectedGroupName('');
        
        // Оновлюємо список фраз та генеруємо конфіг
        await fetchReplacements(contractor?.id);
        await generateConfig(contractor?.id);
      } else {
        setGeneralError(translations?.errorAddingReplacement || 'Помилка при додаванні деяких фраз');
      }
    } catch (error) {
      console.error('Error adding replacements:', error);
      const errorMessage = error.response?.data?.message || (translations?.errorAddingReplacement || 'Помилка при додаванні фрази');
      setGeneralError(errorMessage);
    }
  };

  const handleCancelAdd = () => {
    setNewReplacement({
      name: '',
      phrase: '',
      contractorId: contractor?.id,
      status: 1
    });
    setMultiplePhrases([{ phrase: '' }]);
    setErrors({
      name: '',
      phrase: ''
    });
    setGeneralError('');
    setExistingPhraseMode(false);
    setSelectedGroupName('');
  };

  const handleDeleteReplacement = async (id) => {
    try {
      await axios.delete(`https://bank2.daolog.net/phrases-mmm/${id}`, {
        data: {
          userId: user?.user_id,
          contractorId: contractor?.id
        }
      });
      await fetchReplacements(contractor?.id);
      await generateConfig(contractor?.id);
    } catch (error) {
      console.error('Error deleting replacement:', error);
      setGeneralError(translations?.errorDeletingReplacement || 'Помилка при видаленні фрази');
    }
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(`https://bank2.daolog.net/phrases-mmm/update/${editingReplacement.id}`, {
        ...editingReplacement,
        userId: user?.user_id,
        contractorId: contractor?.id
      });

      if (response.data.success) {
        await fetchReplacements(contractor?.id);
        await generateConfig(contractor?.id);
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error('Error updating replacement:', error);
      setGeneralError(translations?.errorUpdatingReplacement || 'Помилка при оновленні фрази');
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (!translations) return <Typography>{translations?.loading || 'Loading...'}</Typography>;

  return (
    <Box sx={{ p: 3 }} style={{ marginTop: "120px" }}>
      <Typography variant="h6" gutterBottom>
        <b>{translations.client}:</b> {contractor?.name}
      </Typography>

      <Typography variant="h5" gutterBottom style={{ marginTop: "20px" }}>
        {translations.management}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 2 
        }}>
          <TextField
            required
            error={Boolean(errors.name)}
            label={translations.replaceName}
            value={newReplacement.name}
            onChange={(e) => {
              setNewReplacement({ ...newReplacement, name: e.target.value });
              setErrors({ ...errors, name: '' });
            }}
            disabled={existingPhraseMode}
            helperText={errors.name}
            sx={{ width: '400px' }}
          />
          
          {multiplePhrases.map((phrase, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '400px' }}>
              <TextField
                required
                error={Boolean(errors.phrase && !phrase.phrase.trim())}
                label={translations.keyPhrase}
                value={phrase.phrase}
                onChange={(e) => handlePhraseChange(index, e.target.value)}
                helperText={index === 0 && errors.phrase && !phrase.phrase.trim() ? errors.phrase : ''}
                sx={{ flex: 1 }}
              />
              {index === multiplePhrases.length - 1 ? (
                <IconButton onClick={handleAddPhrase} color="primary">
                  <AddIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => handleRemovePhrase(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddReplacement}
            >
              {translations.add}
            </Button>
            {existingPhraseMode && (
              <Button
                variant="outlined"
                onClick={handleCancelAddPhrase}
              >
                {translations.cancel}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {Object.entries(replacements).map(([name, replacementGroup]) => (
          <Box key={name} sx={{ mb: 2 }}>
            <Box 
              sx={{ 
                p: 1, 
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer'
              }}
            >
              <Box 
                sx={{ cursor: 'pointer', flex: 1 }}
                onClick={() => handleExistingPhraseClick(name, replacementGroup[0].status)}
              >
                <Typography><b>{translations.replaceName}:</b> {name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={replacementGroup[0].status === 1}
                      onChange={(e) => handleStatusChange(name, e.target.checked ? 1 : 0)}
                    />
                  }
                  label={replacementGroup[0].status === 1 ? translations.activeConfig : translations.inactiveConfig}
                />
                <IconButton onClick={() => handleOpenEditDialog(replacementGroup[0])} color="primary">
                  <EditIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ pl: 2 }}>
              {replacementGroup.map((replacement) => (
                <Box key={replacement.id} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', mt: 1 }}>
                  <Typography sx={{ flex: 1 }}>{replacement.phrase}</Typography>
                  <IconButton onClick={() => handleDeleteReplacement(replacement.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                  <IconButton onClick={() => handleOpenEditDialog(replacement)} color="primary">
                    <EditIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Paper>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {translations.edit}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={translations.replaceName}
              value={editingReplacement?.name || ''}
              onChange={(e) => setEditingReplacement({ ...editingReplacement, name: e.target.value })}
              fullWidth
            />
            <TextField
              label={translations.keyPhrase}
              value={editingReplacement?.phrase || ''}
              onChange={(e) => setEditingReplacement({ ...editingReplacement, phrase: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingReplacement?.status === 1}
                  onChange={(e) => setEditingReplacement({ ...editingReplacement, status: e.target.checked ? 1 : 0 })}
                />
              }
              label={translations.activeConfig}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>{translations.cancel}</Button>
          <Button onClick={handleSaveEdit} variant="contained">{translations.save}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhraseReplacementPage;

